import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, X, Download, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtNL } from "@/lib/dates";
import { downloadCostReport } from "@/pages/Projecten";

const fmtIso = (s) => (s ? fmtNL(new Date(`${s.slice(0, 10)}T12:00:00`)) : "—");

export function StaleProjects({ onDeleted }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = () => api.get("/projects/stale")
    .then((r) => { setData(r.data); setSelected(new Set(r.data.items.map((p) => p.id))); })
    .catch((err) => console.warn("Oude projecten laden mislukt", err));

  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    setHidden(true);
    setOpen(false);
    try { await api.post("/projects/stale/dismiss"); } catch (err) { console.warn("Melding wegklikken mislukt", err); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.post("/projects/stale/delete", { ids: [...selected] });
      toast.success(`${r.data.deleted} project(en) verwijderd`);
      setOpen(false); setConfirm(false);
      await load();
      onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
    finally { setBusy(false); }
  };

  if (!data || !data.items.length || ((data.dismissed || hidden) && !open)) return null;
  const items = data.items;

  return (
    <>
      {!open && (
        <div className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded border-2 border-slate-300 bg-slate-50 text-xs font-bold text-slate-700" data-testid="stale-banner">
          <Archive size={14} className="shrink-0 text-slate-500"/>
          <span className="flex-1">{items.length} project{items.length === 1 ? "" : "en"} al 3+ maanden niet meer ingepland.</span>
          <button type="button" className="underline text-orange-700 hover:text-orange-900" onClick={() => setOpen(true)} data-testid="stale-open">Bekijken</button>
          <button type="button" className="p-0.5 text-slate-400 hover:text-slate-700" onClick={dismiss} title="Deze maand niet meer tonen" aria-label="Sluiten" data-testid="stale-dismiss"><X size={14}/></button>
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-[80] bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="stale-modal">
          <div className="pb-card bg-white w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-slate-900 flex items-start gap-3">
              <div className="flex-1">
                <div className="pb-heading text-xl font-black">Oude projecten opruimen</div>
                <div className="text-xs text-slate-600 font-medium mt-1">Ouder dan 3 maanden en de laatste 3 maanden niet ingepland. Bij verwijderen gaan ook de kostenregels en oude planningen weg; geregistreerde uren blijven bewaard. Exporteer eerst de kosten als je die nodig hebt.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Sluiten" data-testid="stale-close"><X size={20}/></button>
            </div>
            <label className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 text-xs font-bold">
              <input type="checkbox" checked={selected.size === items.length} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((p) => p.id)) : new Set())} data-testid="stale-select-all"/>
              Alles selecteren ({selected.size}/{items.length})
            </label>
            <div className="overflow-y-auto pb-scrollbar flex-1">
              {items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100" data-testid={`stale-row-${p.id}`}>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} data-testid={`stale-check-${p.id}`}/>
                  <span className="w-3 h-8 rounded-sm border border-slate-900 shrink-0" style={{ background: p.color }}/>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-sm truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[p.customer_name, p.city].filter(Boolean).join(" · ") || "—"} · aangemaakt {fmtIso(p.created_at)} · laatst gepland {p.last_planned ? fmtIso(p.last_planned) : "nooit"}
                    </div>
                  </div>
                  {p.cost_count > 0 && (
                    <button type="button" onClick={() => downloadCostReport(p.id, p.name)} className="pb-btn-outline !min-h-[32px] !px-2 text-xs flex items-center gap-1 shrink-0" title="Kosten exporteren (Excel)" data-testid={`stale-export-${p.id}`}>
                      <Download size={12}/> Kosten
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t-2 border-slate-900 flex flex-wrap items-center justify-between gap-2">
              <button type="button" className="pb-btn-outline" onClick={dismiss} data-testid="stale-later">Later (volgende maand)</button>
              {confirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-red-700">{selected.size} definitief verwijderen?</span>
                  <button type="button" className="pb-btn-outline" onClick={() => setConfirm(false)} data-testid="stale-confirm-no">Nee</button>
                  <button type="button" className="pb-btn-primary !bg-red-600 !border-red-800" disabled={busy} onClick={remove} data-testid="stale-confirm-yes">{busy ? "Bezig…" : "Ja, verwijder"}</button>
                </div>
              ) : (
                <button type="button" className="pb-btn-primary !bg-red-600 !border-red-800 flex items-center gap-1 disabled:opacity-50" disabled={!selected.size} onClick={() => setConfirm(true)} data-testid="stale-delete">
                  <Trash2 size={16}/> Verwijder geselecteerde ({selected.size})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
